import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { loadStorefrontView } from './api';
import { Empty, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import { CHART, ChartTip, KpiRow, Panel, Share, pointLabel, stackRows, tipPoint } from './MobileParts';
import {
  ChannelMark, MoneyText, PathText, ReadState, StoreTable, StorefrontGate, StorefrontHead, channelLabel, clickIdName, count, primaryMoney,
  rate, ratio, st, useStorefrontSite,
} from './StorefrontParts';

// Where the store's visits come from: channels over time, then every source,
// referrer, UTM tag, ad click id, campaign link and landing page, each with the
// sessions, bounce, orders and revenue it brought. Every row opens its sessions.

// Six navy steps, darkest first, for the channels stacked in one chart.
const SHADES = ['#0a1e57', '#203a85', '#4b64a8', '#7185bd', '#a9b5d9', '#d3d9ec'];
const UTM_FIELDS = ['sources', 'mediums', 'campaigns', 'terms', 'contents'];

function sessionsLink(filters) {
  const params = new URLSearchParams(Object.entries(filters).filter(([, value]) => value !== null && value !== undefined && value !== ''));
  return `/xomics/store/sessions?${params}`;
}

// The columns every source table shares, after its own first one.
function acqColumns(total) {
  return [
    {
      key: 'sessions', label: st('acq.col_sessions'), align: 'end', sortValue: (row) => row.sessions,
      render: (row) => <span className="xo-sf-share-cell"><Share value={row.sessions} total={total} /><b>{count(row.sessions)}</b></span>,
    },
    { key: 'visitors', label: st('acq.col_visitors'), align: 'end', wide: true, sortValue: (row) => row.visitors, render: (row) => count(row.visitors) },
    { key: 'bounce', label: st('acq.col_bounce'), align: 'end', wide: true, sortValue: (row) => row.bounceRate, render: (row) => ratio(row.bounceRate) },
    { key: 'orders', label: st('acq.col_orders'), align: 'end', sortValue: (row) => row.orders, render: (row) => count(row.orders) },
    { key: 'conversion', label: st('acq.col_conversion'), align: 'end', sortValue: (row) => row.conversionRate, render: (row) => ratio(row.conversionRate) },
    {
      key: 'revenue', label: st('acq.col_revenue'), align: 'end',
      sortValue: (row) => (primaryMoney(row.revenue) ? primaryMoney(row.revenue).orders : 0), render: (row) => <MoneyText list={row.revenue} />,
    },
  ];
}

function ChannelChart({ data }) {
  const channels = useMemo(() => {
    const totals = new Map();
    for (const point of data.series) for (const [channel, value] of Object.entries(point.channels)) totals.set(channel, (totals.get(channel) || 0) + value);
    return [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([channel]) => channel);
  }, [data.series]);
  const points = useMemo(() => data.series.map((point) => ({ at: point.at, ...point.channels })), [data.series]);
  const parts = channels.map((channel, index) => ({ key: channel, name: channelLabel(channel), color: SHADES[Math.min(index, SHADES.length - 1)] }));
  if (points.every((point) => channels.every((channel) => !point[channel]))) return <Empty title={st('acq.no_visits')} />;
  return (
    <div className="xo-mob-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points} margin={{ top: 8, right: 14, left: 0, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={CHART.grid} />
          <XAxis dataKey="at" tickFormatter={pointLabel(data.step)} tickLine={false} axisLine={false} minTickGap={24} tick={CHART.tick} />
          <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={CHART.tick} width={36} />
          <Tooltip cursor={CHART.cursor} animationDuration={120} content={<ChartTip title={tipPoint(data.step)} rows={(point) => stackRows(point, parts, { sorted: true })} />} />
          {parts.map((part, index) => (
            <Bar key={part.key} dataKey={part.key} stackId="channels" fill={part.color} maxBarSize={22} name={part.name}
              radius={index === parts.length - 1 ? [3, 3, 0, 0] : undefined} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <div className="xo-mob-key">{parts.map((part) => <span key={part.key}><i style={{ background: part.color }} />{part.name}</span>)}</div>
    </div>
  );
}


// Every way of naming where a visit came from, one tab each.
function Sources({ data }) {
  const [view, setView] = useState('sources');
  const [utm, setUtm] = useState('sources');
  const navigate = useNavigate();
  const views = {
    sources: {
      rows: data.sources, key: (row) => `${row.source}|${row.medium}`, open: (row) => ({ source: row.source }),
      first: { key: 'source', label: st('acq.col_source'), primary: true, sortValue: (row) => row.source || '',
        render: (row) => <span className="xo-sf-source"><strong>{row.source || st('acq.direct')}</strong><small>{row.medium || st('acq.no_medium')}</small></span> },
    },
    referrers: {
      rows: data.referrers, key: (row) => row.host, open: (row) => ({ source: row.host }),
      first: { key: 'host', label: st('acq.col_referrer'), primary: true, sortValue: (row) => row.host, render: (row) => <strong className="xo-sf-host">{row.host}</strong> },
    },
    utm: {
      rows: data.utm[utm], key: (row) => row.value, open: () => ({}),
      first: { key: 'value', label: st(`links.utm_${utm.replace(/s$/, '')}`), primary: true, sortValue: (row) => row.value, render: (row) => <strong className="xo-sf-host">{row.value}</strong> },
    },
    clickIds: {
      rows: data.clickIds, key: (row) => row.type, open: () => ({}),
      first: { key: 'type', label: st('acq.col_click_id'), primary: true, sortValue: (row) => row.type,
        render: (row) => <span className="xo-sf-source"><strong>{clickIdName(row.type)}</strong><small>{st('acq.click_id_of', { name: row.type })}</small></span> },
    },
    links: {
      rows: data.campaignLinks, key: (row) => row.link.id, open: (row) => ({ campaign: row.campaign.id, link: row.link.id }),
      first: { key: 'link', label: st('acq.col_link'), primary: true, sortValue: (row) => row.link.code,
        render: (row) => (
          <span className="xo-sf-source">
            <strong>{row.link.label || row.link.code}</strong>
            <small><code>{row.link.code}</code> · <Link to={`/xomics/store/campaigns?campaign=${row.campaign.id}`} onClick={(event) => event.stopPropagation()}>{row.campaign.name}</Link></small>
          </span>
        ) },
    },
    landing: {
      rows: data.landingPages, key: (row) => row.path, open: (row) => ({ landing: row.path }),
      first: { key: 'path', label: st('acq.col_landing'), primary: true, sortValue: (row) => row.path, render: (row) => <PathText path={row.path} /> },
    },
  };
  const current = views[view];
  const total = Math.max(1, ...current.rows.map((row) => row.sessions));
  return (
    <>
      <WorkspaceTabs value={view} onChange={setView} label={st('acq.sources_title')} className="is-compact xo-mob-range xo-sf-tabs" scrollable tabs={[
        { value: 'sources', label: st('acq.tab_sources'), count: data.sources.length },
        { value: 'referrers', label: st('acq.tab_referrers'), count: data.referrers.length },
        { value: 'links', label: st('acq.tab_links'), count: data.campaignLinks.length },
        { value: 'landing', label: st('acq.tab_landing'), count: data.landingPages.length },
        { value: 'utm', label: st('acq.tab_utm') },
        { value: 'clickIds', label: st('acq.tab_click_ids'), count: data.clickIds.length },
      ]} />
      {view === 'utm' && (
        <div className="xo-sf-toggles xo-sf-utm-picker" role="group" aria-label={st('acq.tab_utm')}>
          {UTM_FIELDS.map((field) => (
            <button key={field} type="button" aria-pressed={utm === field} className={utm === field ? 'is-on' : ''} onClick={() => setUtm(field)}>
              {st(`links.utm_${field.replace(/s$/, '')}`)}
            </button>
          ))}
        </div>
      )}
      <StoreTable key={`${view}-${utm}`} columns={[current.first, ...acqColumns(total)]} rows={current.rows} rowKey={current.key} limit={15}
        onRow={view === 'utm' || view === 'clickIds' ? null : (row) => navigate(sessionsLink(current.open(row)))}
        initialSort={{ key: 'sessions', direction: 'desc' }} empty={<Empty title={st('acq.none_here')} />} />
      {view !== 'utm' && view !== 'clickIds' && <p className="xo-mob-footnote">{st('acq.rows_open_sessions')}</p>}
    </>
  );
}

function AcquisitionView({ site, store }) {
  const [days, setDays] = useState(30);
  const navigate = useNavigate();
  const load = useCallback(() => loadStorefrontView('acquisition', { site: site.code, days }), [site.code, days]);
  const report = useLiveResource(['store', 'store_traffic'], load, { reconcileMs: 120000 });
  const head = <StorefrontHead title={st('acq.title')} subtitle={st('acq.subtitle')} days={days} onDays={setDays} store={store} />;
  if (report.data === null) return <div className="xo-platform-page xo-mobile-page xo-sf-page">{head}<ReadState resource={report} label={st('acq.loading')} /></div>;
  const data = report.data;
  const sessions = data.channels.reduce((sum, row) => sum + row.sessions, 0);
  const orders = data.channels.reduce((sum, row) => sum + row.orders, 0);
  const best = [...data.channels].filter((row) => row.sessions >= 20).sort((a, b) => (b.conversionRate || 0) - (a.conversionRate || 0))[0];
  const linkSessions = data.campaignLinks.reduce((sum, row) => sum + row.sessions, 0);
  const channelColumns = [
    { key: 'channel', label: st('acq.col_channel'), primary: true, sortValue: (row) => channelLabel(row.channel), render: (row) => <ChannelMark channel={row.channel} /> },
    ...acqColumns(Math.max(1, ...data.channels.map((row) => row.sessions))),
    { key: 'share', label: st('acq.col_share'), align: 'end', wide: true, sortValue: (row) => row.sessions, render: (row) => rate(row.sessions, sessions) },
  ];
  return (
    <div className="xo-platform-page xo-mobile-page xo-sf-page">
      {head}
      {report.error && <p className="xo-alert t-warn" role="alert">{report.error}</p>}
      <KpiRow items={[
        { label: st('acq.kpi_sessions'), value: count(sessions), detail: st('acq.kpi_channels', { count: data.channels.length }) },
        { label: st('acq.kpi_orders'), value: count(orders), detail: st('acq.kpi_conversion', { rate: rate(orders, sessions) }) },
        { label: st('acq.kpi_best'), value: best ? channelLabel(best.channel) : st('none'), detail: best ? st('acq.kpi_best_detail', { rate: ratio(best.conversionRate) }) : null },
        { label: st('acq.kpi_links'), value: count(linkSessions), detail: st('acq.kpi_links_detail', { share: rate(linkSessions, sessions) }) },
      ]} />
      <Panel title={st('acq.over_time')} detail={st('acq.over_time_detail')}>
        <ChannelChart data={data} />
      </Panel>
      <Panel title={st('acq.channels_title')} detail={st('acq.channels_detail')}>
        <StoreTable columns={channelColumns} rows={data.channels} rowKey={(row) => row.channel} initialSort={{ key: 'sessions', direction: 'desc' }}
          onRow={(row) => navigate(sessionsLink({ channel: row.channel }))} empty={<Empty title={st('acq.no_visits')} />} />
      </Panel>
      <Panel title={st('acq.sources_title')} detail={st('acq.sources_detail')}>
        <Sources data={data} />
      </Panel>
    </div>
  );
}

export default function StorefrontAcquisition() {
  const store = useStorefrontSite();
  return <StorefrontGate store={store}>{(site) => <AcquisitionView site={site} store={store} />}</StorefrontGate>;
}
